import React, { useState, useEffect } from "react";
import "./AddContentModal.css";
import { createContent } from "../../../services/cmsService";

const AddContentModal = ({ onClose, onSave, selectedPage, selectedSection, pages, sections }) => {
  const [formData, setFormData] = useState({
    pageName: selectedPage || "",
    sectionName: selectedSection || "",
    title: "",
    subtitle: "",
    description: "",
    content: "",
    imageUrl: "",
    buttonText: "",
    buttonLink: "",
    sortOrder: 0,
    isActive: true,
    metaTitle: "",
    metaDescription: "",
    metaKeywords: ""
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [availableSections, setAvailableSections] = useState([]);

  useEffect(() => {
    if (formData.pageName) {
      // Filter sections for the selected page
      setAvailableSections(sections.filter(section => 
        section.toLowerCase().includes(formData.pageName.toLowerCase()) || 
        !formData.pageName.includes('-')
      ));
    } else {
      setAvailableSections([]);
    }
  }, [formData.pageName, sections]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.pageName.trim()) {
      newErrors.pageName = "Page name is required";
    }
    
    if (!formData.sectionName.trim()) {
      newErrors.sectionName = "Section name is required";
    }
    
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }
    
    if (formData.imageUrl && !isValidUrl(formData.imageUrl)) {
      newErrors.imageUrl = "Please enter a valid URL";
    }
    
    if (formData.buttonLink && !isValidUrl(formData.buttonLink)) {
      newErrors.buttonLink = "Please enter a valid URL";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      await createContent(formData);
      onSave();
    } catch (error) {
      console.error("Error creating content:", error);
      setErrors({ submit: "Failed to create content. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add New Content</h2>
          <button className="close-button" onClick={handleClose} disabled={loading}>
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="content-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="pageName">Page Name *</label>
              <select
                id="pageName"
                name="pageName"
                value={formData.pageName}
                onChange={handleInputChange}
                className={errors.pageName ? "error" : ""}
                disabled={loading}
              >
                <option value="">Select a page</option>
                {pages.map(page => (
                  <option key={page} value={page}>{page}</option>
                ))}
              </select>
              {errors.pageName && <span className="error-message">{errors.pageName}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="sectionName">Section Name *</label>
              <select
                id="sectionName"
                name="sectionName"
                value={formData.sectionName}
                onChange={handleInputChange}
                className={errors.sectionName ? "error" : ""}
                disabled={loading || !formData.pageName}
              >
                <option value="">Select a section</option>
                {availableSections.map(section => (
                  <option key={section} value={section}>{section}</option>
                ))}
              </select>
              {errors.sectionName && <span className="error-message">{errors.sectionName}</span>}
            </div>
            
            <div className="form-group full-width">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={errors.title ? "error" : ""}
                disabled={loading}
                placeholder="Enter content title"
              />
              {errors.title && <span className="error-message">{errors.title}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="subtitle">Subtitle</label>
              <input
                type="text"
                id="subtitle"
                name="subtitle"
                value={formData.subtitle}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="Enter subtitle"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="sortOrder">Sort Order</label>
              <input
                type="number"
                id="sortOrder"
                name="sortOrder"
                value={formData.sortOrder}
                onChange={handleInputChange}
                disabled={loading}
                min="0"
              />
            </div>
            
            <div className="form-group full-width">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="Enter content description"
                rows="3"
              />
            </div>
            
            <div className="form-group full-width">
              <label htmlFor="content">Content</label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="Enter main content"
                rows="5"
              />
            </div>
            
            <div className="form-group full-width">
              <label htmlFor="imageUrl">Image URL</label>
              <input
                type="url"
                id="imageUrl"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={handleInputChange}
                className={errors.imageUrl ? "error" : ""}
                disabled={loading}
                placeholder="https://example.com/image.jpg"
              />
              {errors.imageUrl && <span className="error-message">{errors.imageUrl}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="buttonText">Button Text</label>
              <input
                type="text"
                id="buttonText"
                name="buttonText"
                value={formData.buttonText}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="Learn More"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="buttonLink">Button Link</label>
              <input
                type="url"
                id="buttonLink"
                name="buttonLink"
                value={formData.buttonLink}
                onChange={handleInputChange}
                className={errors.buttonLink ? "error" : ""}
                disabled={loading}
                placeholder="https://example.com"
              />
              {errors.buttonLink && <span className="error-message">{errors.buttonLink}</span>}
            </div>
            
            <div className="form-group full-width">
              <label htmlFor="metaTitle">Meta Title</label>
              <input
                type="text"
                id="metaTitle"
                name="metaTitle"
                value={formData.metaTitle}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="SEO meta title"
              />
            </div>
            
            <div className="form-group full-width">
              <label htmlFor="metaDescription">Meta Description</label>
              <textarea
                id="metaDescription"
                name="metaDescription"
                value={formData.metaDescription}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="SEO meta description"
                rows="2"
              />
            </div>
            
            <div className="form-group full-width">
              <label htmlFor="metaKeywords">Meta Keywords</label>
              <input
                type="text"
                id="metaKeywords"
                name="metaKeywords"
                value={formData.metaKeywords}
                onChange={handleInputChange}
                disabled={loading}
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>
            
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  disabled={loading}
                />
                <span className="checkmark"></span>
                Active (visible on website)
              </label>
            </div>
          </div>
          
          {errors.submit && (
            <div className="error-message submit-error">
              {errors.submit}
            </div>
          )}
          
          <div className="modal-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="save-button"
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Content"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContentModal;
